import { get, post, getApiBaseUrl } from './client';
import type { User, Team, TeamMember } from '@/types/api';

const PRODUCTION_AUTH_BASE_URL = 'https://web-production-ff855.up.railway.app';

export interface AuthResponse {
  user: User;
  teams: Team[];
}

export interface LoginResponse {
  token: string;
}

export async function getCurrentUser(): Promise<User> {
  return get<User>('/api/user/me');
}

export async function getTeams(): Promise<Team[]> {
  return get<Team[]>('/api/teams');
}

export async function logout(): Promise<void> {
  await post('/api/auth/logout');
}

export function getGoogleAuthUrl(): string {
  // OAuth must be a top-level navigation to the backend. Vercel rewrites can
  // leave Google's redirect inside the rewritten frame context, which browsers block.
  const isLocal =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = isLocal ? getApiBaseUrl() || window.location.origin : PRODUCTION_AUTH_BASE_URL;
  const authUrl = new URL('/auth/google', baseUrl);
  authUrl.searchParams.set('redirect_to', window.location.origin);
  return authUrl.toString();
}

export async function loginWithCredentials(
  username: string,
  password: string
): Promise<LoginResponse> {
  return post<LoginResponse>('/api/auth/login', { username, password });
}

export async function createTeam(name: string): Promise<Team> {
  return post<Team>('/api/teams', { name });
}

export async function inviteTeamMember(
  teamId: string,
  email: string,
  role: 'admin' | 'member'
): Promise<{ message: string }> {
  return post<{ message: string }>(`/api/teams/${teamId}/invite`, { email, role });
}

export async function removeTeamMember(
  teamId: string,
  memberId: string
): Promise<{ message: string }> {
  return post<{ message: string }>(`/api/teams/${teamId}/members/${memberId}/remove`);
}

export async function updateMemberRole(
  teamId: string,
  memberId: string,
  role: 'admin' | 'member'
): Promise<TeamMember> {
  return post<TeamMember>(`/api/teams/${teamId}/members/${memberId}/role`, { role });
}
